import React, { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import { REGISTRATIONS_LIST, REGISTRATION_DELETE } from '../../graphql/registrations';
import { VEHICLES_LIST } from '../../graphql/vehicles';
import RegistrationForm from './RegistrationForm';
import Pagination from '../../components/Pagination';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Button, 
  IconButton, 
  Box, 
  Typography, 
  MenuItem, 
  TextField, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Link,
  Chip,
  Avatar,
  Card,
  CardContent,
  InputAdornment,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Description,
  Warning,
  CheckCircle,
  Schedule,
  DirectionsCar,
  AttachFile
} from '@mui/icons-material';
import dayjs from 'dayjs';

const getStatusColor = (annualDate, expiryDate) => {
  const now = dayjs();
  const annual = dayjs(annualDate);
  const expiry = dayjs(expiryDate);
  
  if (now.isAfter(expiry)) return { bg: '#ef4444', color: 'white' }; // Expired
  if (now.isAfter(annual)) return { bg: '#f59e0b', color: 'white' }; // Due for renewal
  return { bg: '#10b981', color: 'white' }; // Active
};

const getStatusIcon = (annualDate, expiryDate) => {
  const now = dayjs();
  const annual = dayjs(annualDate);
  const expiry = dayjs(expiryDate);
  
  if (now.isAfter(expiry)) return <Warning sx={{ fontSize: 16 }} />;
  if (now.isAfter(annual)) return <Schedule sx={{ fontSize: 16 }} />;
  return <CheckCircle sx={{ fontSize: 16 }} />;
};

const RegistrationsList = ({ vehicleId: propVehicleId, onEdit }) => {
  const [openForm, setOpenForm] = useState(false);
  const [editRegistration, setEditRegistration] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, registration: null });
  const [searchParams, setSearchParams] = useSearchParams();

  // Get pagination and filter state from URL
  const currentPage = parseInt(searchParams.get('page')) || 1;
  const itemsPerPage = parseInt(searchParams.get('limit')) || 10;
  const search = searchParams.get('search') || '';
  const selectedVehicle = searchParams.get('vehicle') || propVehicleId || '';

  // Update URL parameters
  const updateURLParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value && value !== 'All' && value !== '') {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  };

  // Handle filter changes
  const handleSearchChange = (value) => {
    updateURLParams({ search: value, page: 1 });
  };

  const handleVehicleChange = (value) => {
    updateURLParams({ vehicle: value, page: 1 });
  };

  const handlePageChange = (page) => {
    updateURLParams({ page });
  };

  const handleItemsPerPageChange = (limit) => {
    updateURLParams({ limit, page: 1 });
  };

  const { data: vehiclesData } = useQuery(VEHICLES_LIST);
  const { data, loading, refetch } = useQuery(REGISTRATIONS_LIST, {
    variables: { 
      vehicleId: selectedVehicle || undefined,
      first: itemsPerPage,
      skip: (currentPage - 1) * itemsPerPage,
      sort: { createdAt: 'DESC' }
    },
  });
  const [deleteRegistration] = useMutation(REGISTRATION_DELETE, {
    onCompleted: () => refetch(),
  });

  const handleEdit = (registration) => {
    if (onEdit) {
      onEdit(registration);
    } else {
      setEditRegistration(registration);
      setOpenForm(true);
    }
  };

  const handleCreate = () => {
    setEditRegistration(null);
    setOpenForm(true);
  };

  const handleFormClose = (refresh) => {
    setOpenForm(false);
    setEditRegistration(null);
    if (refresh) refetch();
  };

  const handleDeleteClick = (registration) => {
    setDeleteDialog({ open: true, registration });
  };

  const handleDeleteConfirm = async () => {
    await deleteRegistration({ variables: { filter: { id:deleteDialog.registration.id } }  });
    setDeleteDialog({ open: false, registration: null });
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, registration: null });
  };

  const filteredRegistrations = useMemo(() => {
    if (!data?.registrationsList?.items) return [];
    const registrations = data.registrationsList.items;
    return registrations.filter((reg) => {
      const matchesSearch = 
        reg.registrationNumber?.toLowerCase().includes(search.toLowerCase()) ||
        reg.id?.toLowerCase().includes(search.toLowerCase()) ||
        (reg.registrations?.make && reg.registrations.make.toLowerCase().includes(search.toLowerCase())) ||
        (reg.registrations?.model && reg.registrations.model.toLowerCase().includes(search.toLowerCase()));
      return matchesSearch;
    });
  }, [data, search]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Search and Filters */}
      <Card sx={{ 
        mb: 3, 
        borderRadius: 3, 
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        border: '1px solid #e2e8f0'
      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FilterIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Search & Filters
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Search Registrations"
              value={search}
              onChange={e => handleSearchChange(e.target.value)}
              placeholder="Search by registration number, ID, or vehicle"
              size="small"
              sx={{ minWidth: 300, flexGrow: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              label="Vehicle"
              value={selectedVehicle}
              onChange={e => handleVehicleChange(e.target.value)}
              size="small"
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">All Vehicles</MenuItem>
              {vehiclesData?.vehiclesList?.items?.map(v => (
                <MenuItem key={v.id} value={v.id}>{v.make} {v.model} ({v.licensePlate})</MenuItem>
              ))}
            </TextField>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleCreate}
              sx={{
                background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)'
                }
              }}
            >
              Add Registration
            </Button>
          </Box>
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Found {data?.registrationsList?.count || 0} registrations
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Registration Table */}
      <Card sx={{ 
        borderRadius: 3, 
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        border: '1px solid #e2e8f0',
        overflow: 'hidden'
      }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Registration #</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Annual Date</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Expiry Date</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Vehicle</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Documents</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRegistrations.map(reg => {
                const statusConfig = getStatusColor(reg.annualDate, reg.expiryDate);
                
                return (
                  <TableRow 
                    key={reg.id} 
                    hover 
                    onClick={() => handleEdit(reg)} 
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: 'rgba(6, 182, 212, 0.04)',
                        transform: 'scale(1.01)',
                      }
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar 
                          sx={{ 
                            bgcolor: '#06b6d4', 
                            width: 32, 
                            height: 32, 
                            mr: 2,
                            boxShadow: '0 2px 8px rgba(6, 182, 212, 0.3)'
                          }}
                        >
                          <Description sx={{ fontSize: 16 }} />
                        </Avatar>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {reg.registrationNumber || 'No number'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(reg.annualDate, reg.expiryDate)}
                        label={dayjs().isAfter(dayjs(reg.expiryDate)) ? 'Expired' : 
                               dayjs().isAfter(dayjs(reg.annualDate)) ? 'Due for Renewal' : 'Active'}
                        size="small"
                        sx={{
                          bgcolor: statusConfig.bg,
                          color: statusConfig.color,
                          fontWeight: 600,
                          '& .MuiChip-icon': {
                            color: statusConfig.color
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {reg.annualDate ? dayjs(reg.annualDate).format('MMM DD, YYYY') : 'Not set'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {reg.expiryDate ? dayjs(reg.expiryDate).format('MMM DD, YYYY') : 'Not set'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar 
                          sx={{ 
                            bgcolor: '#6366f1', 
                            width: 24, 
                            height: 24, 
                            mr: 1,
                            boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)'
                          }}
                        >
                          <DirectionsCar sx={{ fontSize: 12 }} />
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {reg.registrations ? `${reg.registrations.make} ${reg.registrations.model}` : 'No vehicle'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {reg.document && reg.document.items.length > 0 ? (
                        reg.document.items.map(doc => (
                          <Box key={doc.id} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <Avatar 
                              sx={{ 
                                bgcolor: '#10b981', 
                                width: 20, 
                                height: 20, 
                                mr: 1,
                                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                              }}
                            >
                              <AttachFile sx={{ fontSize: 10 }} />
                            </Avatar>
                            <Link 
                              href={doc.downloadUrl} 
                              target="_blank" 
                              rel="noopener"
                              sx={{ 
                                fontSize: '0.75rem',
                                color: '#06b6d4',
                                textDecoration: 'none',
                                '&:hover': {
                                  textDecoration: 'underline'
                                }
                              }}
                            >
                              {doc.filename}
                            </Link>
                          </Box>
                        ))
                      ) : (
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                          No documents
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Edit Registration">
                          <IconButton 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(reg);
                            }} 
                            size="small" 
                            sx={{
                              color: '#06b6d4',
                              '&:hover': {
                                bgcolor: 'rgba(6, 182, 212, 0.1)'
                              }
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Registration">
                          <IconButton 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(reg);
                            }} 
                            size="small" 
                            sx={{
                              color: '#ef4444',
                              '&:hover': {
                                bgcolor: 'rgba(239, 68, 68, 0.1)'
                              }
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={Math.ceil((data?.registrationsList?.count || 0) / itemsPerPage)}
          totalItems={data?.registrationsList?.count || 0}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          itemsPerPageOptions={[10, 25, 50, 100]}
        />
      </Card>

      {/* Form Dialog */}
      <Dialog 
        open={openForm} 
        onClose={() => handleFormClose(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
          color: 'white',
          fontWeight: 600
        }}>
          {editRegistration ? 'Edit Registration' : 'Add Registration'}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <RegistrationForm 
            registration={editRegistration} 
            onClose={handleFormClose} 
            vehicleId={selectedVehicle} 
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialog.open} 
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          color: 'white',
          fontWeight: 600
        }}>
          Delete Registration
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography>
            Are you sure you want to delete this registration? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleDeleteCancel} variant="outlined">
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RegistrationsList; 